import React, { useState, useEffect } from 'react';
import { getAxiomAnalytics } from '../lib/api';
import { 
  MessageCircle, Users, TrendingUp, Target, Sparkles,
  BarChart3, Calendar, FileText
} from 'lucide-react';

export default function AxiomAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await getAxiomAnalytics();
      setAnalytics(response.data);
    } catch (err) {
      setError('Failed to load Axiom analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        {error}
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const topicColors = {
    framework: 'bg-purple-100 text-purple-800',
    documentation: 'bg-blue-100 text-blue-800',
    assessment: 'bg-green-100 text-green-800',
    guidance: 'bg-amber-100 text-amber-800',
  };

  const conversionLabels = {
    documentation_system: 'Documentation System',
    assessment: 'Assessment',
    declaration: 'Declaration',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#102A43] to-[#334E68] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#102A43]">Axiom Analytics</h2>
            <p className="text-sm text-[#64748B]">AI Governance Intelligence Layer</p>
          </div>
        </div>
        <button 
          onClick={loadAnalytics}
          className="text-sm text-[#2F80ED] hover:underline"
        >
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-[#64748B]">Total Messages</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{analytics.total_messages.toLocaleString()}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{analytics.messages_today} today</p>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-sm text-[#64748B]">Conversations</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{analytics.total_conversations.toLocaleString()}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{analytics.messages_week} msgs this week</p>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-sm text-[#64748B]">Active Users</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">{analytics.active_users_today}</p>
          <p className="text-xs text-[#94A3B8] mt-1">{analytics.active_users_week} this week</p>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Target className="w-4 h-4 text-amber-600" />
            </div>
            <span className="text-sm text-[#64748B]">Conversions</span>
          </div>
          <p className="text-2xl font-bold text-[#102A43]">
            {Object.values(analytics.conversion_events || {}).reduce((a, b) => a + b, 0)}
          </p>
          <p className="text-xs text-[#94A3B8] mt-1">product opportunities</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Daily Usage Chart */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-[#64748B]" />
            <h3 className="font-medium text-[#102A43]">Daily Usage (Last 7 Days)</h3>
          </div>
          <div className="flex items-end gap-2 h-32">
            {analytics.daily_usage.slice().reverse().map((day, idx) => {
              const maxMessages = Math.max(...analytics.daily_usage.map(d => d.messages), 1);
              const height = (day.messages / maxMessages) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-[#2F80ED] rounded-t transition-all hover:bg-[#1E6FD9]"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${day.messages} messages`}
                  ></div>
                  <span className="text-[10px] text-[#94A3B8] mt-1">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Topics */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[#64748B]" />
            <h3 className="font-medium text-[#102A43]">Top Topics</h3>
          </div>
          <div className="space-y-3">
            {analytics.top_topics.length > 0 ? (
              analytics.top_topics.slice(0, 5).map((topic, idx) => {
                const maxCount = analytics.top_topics[0]?.count || 1;
                const width = (topic.count / maxCount) * 100;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${topicColors[topic.topic] || 'bg-gray-100 text-gray-800'}`}>
                      {topic.topic}
                    </span>
                    <div className="flex-1 bg-[#F0F4F8] rounded-full h-2">
                      <div 
                        className="bg-[#2F80ED] h-2 rounded-full transition-all"
                        style={{ width: `${width}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-[#64748B] w-8 text-right">{topic.count}</span>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-[#94A3B8] text-center py-4">No topic data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Conversion Events */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#64748B]" />
          <h3 className="font-medium text-[#102A43]">Conversion Opportunities</h3>
          <span className="text-xs text-[#94A3B8]">(Topics that may lead to product interest)</span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(conversionLabels).map(([key, label]) => {
            const count = analytics.conversion_events?.[key] || 0;
            return (
              <div key={key} className="text-center p-4 bg-[#F8FAFC] rounded-lg">
                <p className="text-2xl font-bold text-[#102A43]">{count}</p>
                <p className="text-xs text-[#64748B] mt-1">{label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
